// Lightweight CSS-driven confetti overlay. Zero external libraries; each piece
// is one absolutely-positioned div animated via the `confetti-fall` keyframe.
//
// Intentionally single-file and small (~60 pieces) — a 7-year-old's reaction
// to chapter-complete should feel generous but not performance-janky on a
// mid-range iPad. The overlay is pointer-events:none so it never blocks taps.
//
// Silenced automatically by the global `prefers-reduced-motion: reduce` rule
// in index.css.

import { useMemo } from 'react';

const CONFETTI_COLORS = [
  'var(--amber, #c96a2f)',
  'var(--amber-deep, #a64f1f)',
  '#f5d67b',
  '#ffb74d',
  '#4caf50',
  '#e57373',
  '#64b5f6',
  '#ba68c8',
];

interface Piece {
  leftPct: number;
  delayMs: number;
  durationMs: number;
  color: string;
  rotateStart: number;
  rotateEnd: number;
  widthPx: number;
  heightPx: number;
}

function buildPieces(count: number, seed: number): Piece[] {
  // Deterministic LCG seeded on the mount key — re-mounting with a new key
  // triggers a fresh pattern, same component gets a stable pattern.
  let s = seed || 1;
  const next = () => { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 0xffffffff; };
  const pieces: Piece[] = [];
  for (let i = 0; i < count; i++) {
    pieces.push({
      leftPct: next() * 100,
      delayMs: Math.floor(next() * 600),
      durationMs: 1400 + Math.floor(next() * 900),
      color: CONFETTI_COLORS[Math.floor(next() * CONFETTI_COLORS.length)],
      rotateStart: Math.floor(next() * 360),
      rotateEnd: Math.floor(360 + next() * 720),
      widthPx: 6 + Math.floor(next() * 6),
      heightPx: 10 + Math.floor(next() * 8),
    });
  }
  return pieces;
}

interface Props {
  /** Changes re-seed and re-trigger the animation. */
  seedKey?: number;
  /** Piece count; default tuned for iPad viewport. */
  count?: number;
}

export default function Confetti({ seedKey = 1, count = 60 }: Props) {
  const pieces = useMemo(() => buildPieces(count, seedKey), [count, seedKey]);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 21,
      }}
    >
      {pieces.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${p.leftPct}%`,
            top: -20,
            width: p.widthPx,
            height: p.heightPx,
            background: p.color,
            borderRadius: 1,
            opacity: 0,
            animation: `confetti-fall ${p.durationMs}ms ease-in ${p.delayMs}ms forwards`,
            // Pass per-piece rotation range via CSS custom properties so one
            // shared keyframe can animate to different end-rotations.
            ['--rot-start' as never]: `${p.rotateStart}deg`,
            ['--rot-end' as never]: `${p.rotateEnd}deg`,
          }}
        />
      ))}
    </div>
  );
}
